/**
 * WeaveIn.ai Platform - Universal Audit Trail Panel
 * Reusable component showing change history for any entity type.
 * Used in contacts, members, enrollments, and forms.
 */

import React, { useState, useEffect } from 'react';
import {
  Clock, User, ChevronDown, ChevronUp, Loader2, History, FileText,
  Upload, PenTool, Trash2, ArrowRight, MessageSquare, CheckCircle, Star,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

interface AuditEntry {
  id: number;
  action: string;
  field_changes: Record<string, { old: any; new: any }>;
  performed_by_name?: string;
  performed_at?: string;
  notes?: string;
  version_number?: number;
  entity_name?: string;
}

interface AuditTrailPanelProps {
  entityType: string;
  entityId: number;
  apiPath?: string;
}

const ACTION_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  created: { label: 'Created', color: 'bg-green-100 text-green-700 border-green-200', icon: Star },
  updated: { label: 'Updated', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: PenTool },
  deleted: { label: 'Deleted', color: 'bg-red-100 text-red-700 border-red-200', icon: Trash2 },
  status_changed: { label: 'Status Changed', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ArrowRight },
  promoted: { label: 'Promoted', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  merged: { label: 'Merged', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: ArrowRight },
  restored: { label: 'Restored', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: History },
};

const formatFieldName = (field: string): string => {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatValue = (val: any): string => {
  if (val == null || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

const formatTimestamp = (ts: string): string => {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function AuditTrailPanel({ entityType, entityId, apiPath }: AuditTrailPanelProps) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const path = apiPath || `/api/contacts/audit-log/${entityType}/${entityId}`;
        const res = await apiClient.get(path);
        setEntries(Array.isArray(res.data) ? res.data : []);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entityType, entityId, apiPath]);

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 justify-center text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history...
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No change history recorded yet</p>
        <p className="text-xs text-gray-300 mt-1">
          Changes will appear here as you create, update, or manage records.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => {
        const config = ACTION_CONFIG[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock };
        const Icon = config.icon;
        const isExpanded = expandedIds.has(entry.id);
        const changeCount = Object.keys(entry.field_changes || {}).length;
        const hasDetail = changeCount > 0 || entry.notes;

        return (
          <div key={entry.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {/* Header row */}
            <button
              onClick={() => hasDetail && toggleExpanded(entry.id)}
              className={`w-full flex items-start justify-between px-4 py-3 text-left ${
                hasDetail ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer' : 'cursor-default'
              }`}
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {/* Action icon */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${config.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>

                <div className="min-w-0 flex-1">
                  {/* Action + entity name */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${config.color}`}>
                      {config.label}
                    </span>
                    {entry.entity_name && (
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                        {entry.entity_name}
                      </span>
                    )}
                    {entry.version_number && (
                      <span className="text-xs text-gray-400">v{entry.version_number}</span>
                    )}
                  </div>

                  {/* Notes summary — shown directly on the header row */}
                  {entry.notes && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-snug">
                      {entry.notes}
                    </p>
                  )}

                  {/* Change count hint */}
                  {changeCount > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {changeCount} field{changeCount !== 1 ? 's' : ''} changed
                      {!isExpanded && ' — click to view details'}
                    </p>
                  )}
                </div>
              </div>

              {/* Right side: user + time */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                <div className="text-right">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <User className="w-3 h-3" />
                    {entry.performed_by_name || 'System'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {entry.performed_at ? formatTimestamp(entry.performed_at) : ''}
                  </div>
                </div>
                {hasDetail && (
                  isExpanded
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </button>

            {/* Expanded detail: field changes table */}
            {isExpanded && changeCount > 0 && (
              <div className="px-4 pb-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="text-gray-400 text-xs">
                      <th className="text-left py-1 pr-4 font-medium w-1/3">Field</th>
                      <th className="text-left py-1 pr-4 font-medium w-1/3">Previous Value</th>
                      <th className="text-left py-1 font-medium w-1/3">New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(entry.field_changes).map(([field, change]) => (
                      <tr key={field} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-4 font-medium text-gray-600 dark:text-gray-400 text-xs">
                          {formatFieldName(field)}
                        </td>
                        <td className="py-2 pr-4 text-xs">
                          {change.old != null && change.old !== '' ? (
                            <span className="text-red-500 line-through bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                              {formatValue(change.old)}
                            </span>
                          ) : (
                            <span className="text-gray-300 italic">empty</span>
                          )}
                        </td>
                        <td className="py-2 text-xs">
                          <span className="text-green-600 font-medium bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                            {formatValue(change.new)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
